// Every text box on the Breeze bar and the prep chat: an editable area that
// takes the prep editor's shortcuts. Ctrl or Cmd with 8 makes a bullet, with B
// bold, and with H a highlight. What it keeps is text (lib/richText.ts), so the
// people and the model reading it see what was written.
//
// A single-line box (a question, an item, the chat input) takes Enter as its
// submit and never holds a line break; Ctrl+8 there puts a bullet at the front.

import { useLayoutEffect, useRef, type JSX } from "react";
import { handleKey, pastePlain } from "../lib/prepDom";
import { sanitizeHtml, toEditable } from "../lib/prepEdit";
import { htmlToText, textToHtml } from "../lib/richText";

interface TextBoxProps {
  readonly value: string;
  readonly onValue: (text: string) => void;
  readonly className?: string;
  readonly id?: string;
  readonly rows?: number;
  readonly maxLength?: number;
  readonly placeholder?: string;
  readonly ariaLabel?: string;
  readonly disabled?: boolean;
  readonly singleLine?: boolean;
  readonly autoFocus?: boolean;
  /** Enter without Shift, in a single-line box or a chat box. */
  readonly onEnter?: () => void;
  readonly onEscape?: () => void;
  readonly onBlur?: (text: string) => void;
  readonly testId?: string;
}

const LINE_EM = 1.4;

export function TextBox({
  value,
  onValue,
  className,
  id,
  rows,
  maxLength,
  placeholder,
  ariaLabel,
  disabled,
  singleLine,
  autoFocus,
  onEnter,
  onEscape,
  onBlur,
  testId,
}: TextBoxProps): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  /** The last text this box sent up, so its own echo never resets the caret. */
  const sent = useRef<string | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || value === sent.current) return;
    sent.current = value;
    el.innerHTML = toEditable(textToHtml(value));
  }, [value]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!autoFocus || !el) return;
    el.focus();
    caretToEnd(el);
  }, [autoFocus]);

  const read = (): string => {
    const el = ref.current;
    if (!el) return "";
    const text = htmlToText(sanitizeHtml(el.innerHTML));
    return singleLine ? text.replace(/\s*\n\s*/g, " ").trim() : text;
  };

  const emit = (): void => {
    let text = read();
    if (maxLength !== undefined && text.length > maxLength) text = text.slice(0, maxLength);
    sent.current = text;
    onValue(text);
  };

  const minHeight = !singleLine && rows ? `calc(${rows * LINE_EM}em + 20px)` : undefined;

  return (
    <div
      ref={ref}
      id={id}
      className={`tb${singleLine ? " tb--line" : ""}${value === "" ? " tb--empty" : ""}${className ? ` ${className}` : ""}`}
      style={minHeight ? { minHeight } : undefined}
      contentEditable={!disabled}
      suppressContentEditableWarning
      role="textbox"
      tabIndex={disabled ? -1 : 0}
      aria-multiline={singleLine ? "false" : "true"}
      aria-label={ariaLabel}
      aria-disabled={disabled ? "true" : undefined}
      data-placeholder={placeholder}
      data-testid={testId}
      enterKeyHint={onEnter ? "send" : undefined}
      onInput={emit}
      onBlur={() => onBlur?.(read())}
      onKeyDown={(e) => {
        const ne = e.nativeEvent;
        if (e.key === "Escape" && onEscape) {
          e.preventDefault();
          e.stopPropagation();
          onEscape();
          return;
        }
        if (e.key === "Enter" && !e.shiftKey && !ne.isComposing && (singleLine || onEnter)) {
          e.preventDefault();
          onEnter?.();
          return;
        }
        if (singleLine) {
          const mod = (e.ctrlKey || e.metaKey) && !e.altKey;
          if (!mod) return;
          if (e.key === "8" || e.key === "*") {
            e.preventDefault();
            const text = read();
            const el = e.currentTarget;
            el.innerHTML = toEditable(textToHtml(text.startsWith("• ") ? text.slice(2) : `• ${text}`));
            caretToEnd(el);
            emit();
            return;
          }
        }
        handleKey(ne, e.currentTarget);
      }}
      onPaste={(e) => {
        e.preventDefault();
        const text = e.clipboardData.getData("text/plain");
        if (singleLine) {
          try {
            document.execCommand("insertText", false, text.replace(/\s*\r?\n\s*/g, " "));
          } catch {
            // Falls through with nothing inserted.
          }
          return;
        }
        pastePlain(e.currentTarget, text);
      }}
      onDrop={(e) => {
        if (e.dataTransfer.files.length > 0) e.preventDefault();
      }}
    />
  );
}

function caretToEnd(el: HTMLElement): void {
  const r = document.createRange();
  r.selectNodeContents(el);
  r.collapse(false);
  const sel = window.getSelection();
  sel?.removeAllRanges();
  sel?.addRange(r);
}
